import React from "react";
import { Box } from "@mui/material";
import Section from "../ui/Section";
import Button from "../ui/Button";
import { Accent, Display, Label } from "../ui/Type";
import { COMPANY } from "../../data/company";
import { color, motion, type } from "../../design/tokens";

/**
 * Closing call to action.
 *
 * The last ink band on every page. Deliberately spare — an oversized line, two
 * actions, and the ways to reach a human. The phone numbers are here as well
 * as in the footer because a good share of renovation enquiries are a phone
 * call, not a form, and burying the number below a form costs those.
 */
const CTABand = ({
  title,
  lead = "Tell us what you're planning and we'll come and look at the space. No charge for the visit or the quote.",
  primaryLabel = "Get a Free Quote",
}) => (
  <Section tone="ink">
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "1.2fr 0.8fr" },
        gap: { xs: 5, lg: 10 },
        alignItems: "end",
      }}
    >
      <Box>
        <Label tone="onInk">Start here</Label>
        <Display as="h2" size="xl" tone="onInk" sx={{ marginTop: 3 }}>
          {title || (
            <>
              Let's walk the <Accent sx={{ color: color.clayLight }}>space</Accent>.
            </>
          )}
        </Display>
        <Box
          component="p"
          sx={{ ...type.lead, color: color.onInkMute, marginTop: 3, maxWidth: 520 }}
        >
          {lead}
        </Box>
      </Box>

      <Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <Button to="/contact" variant="onInk" size="lg">
            {primaryLabel}
          </Button>
          <Button href={`tel:${COMPANY.phone.primaryE164}`} variant="onInkGhost" size="lg">
            {COMPANY.phone.primary}
          </Button>
        </Box>

        <Box
          sx={{
            marginTop: 4,
            paddingTop: 3,
            borderTop: `1px solid ${color.lineOnInk}`,
            display: "grid",
            gap: 1,
          }}
        >
          <Box
            component="a"
            href={`mailto:${COMPANY.email}`}
            sx={{
              ...type.bodySm,
              color: color.onInkMute,
              wordBreak: "break-word",
              transition: `color ${motion.base} ${motion.ease}`,
              "&:hover": { color: color.onInk },
            }}
          >
            {COMPANY.email}
          </Box>
          <Box component="p" sx={{ ...type.caption, color: color.onInkMute, margin: 0 }}>
            Serving Pickering, Ajax, Whitby and the wider Durham Region.
          </Box>
        </Box>
      </Box>
    </Box>
  </Section>
);

export default CTABand;
